import { Component, OnInit } from '@angular/core';
import { MenuItem, MessageService, ConfirmationService } from 'primeng/api'; // Import ConfirmationService
import { RouterModule, Router } from '@angular/router'; // Import Router
import { CommonModule } from '@angular/common';

import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { StyleClassModule } from 'primeng/styleclass';
import { OverlayPanelModule } from 'primeng/overlaypanel';

import { AppConfigurator } from './app.configurator';

import { DirectionService } from '../../services/direction.service';
import { ExitService } from '../../services/exit.service';
import { LayoutService } from '../service/layout.service';
import { TranslateService } from '../../services/translate.service';
import { UserPopoverComponent } from './user-popover/user-popover.component';
import { AuthStateService } from '../../services/auth-state.service';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    StyleClassModule,
    AppConfigurator,
    OverlayPanelModule,
    DialogModule,
    ConfirmDialogModule,
    UserPopoverComponent
  ],
  styleUrl: './app.topbar.scss',
  templateUrl: './app.topbar.html',
  providers: [MessageService, ConfirmationService]
})
export class AppTopbar implements OnInit{
  items!: MenuItem[];
  currentLang: string = 'en';
  
    // The change is here: we initialize the Observable with a default value.
  public titleMsg$: Observable<string> = of('');

  constructor(
    public layoutService: LayoutService,
    public exitService: ExitService,
    private translateServ: TranslateService,
    private directionService:DirectionService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private authStateService: AuthStateService
  ) {}

  ngOnInit(): void
  {
    this.currentLang = this.authStateService.getLanguage() || 'en';
    this.buildTitleMsg();
  }

  toggleDarkMode() {
    this.layoutService.layoutConfig.update((state) => ({ ...state, darkTheme: !state.darkTheme }));
  }

  changeLang(lang: 'he' | 'en') 
  {
    const toChange = this.directionService.updateLanguage(lang);

    if(toChange)
    {
      // updateLanguage() persists the change server-side and reloads the page once it succeeds;
      // the reload's whoami check will pick up the new language, no need to cache it here.
      this.currentLang = lang;
      this.directionService.setDirection(lang === 'en' ? 'ltr' : "rtl");
      this.buildTitleMsg();
    }
  }

    private buildTitleMsg(): void
    {
        this.titleMsg$ = this.getName().pipe(
            map(name => {
                return this.translateServ.dictionary['APP_NAME'] + " | " + this.getGreeting() + ' ' + name;
            })
        );
    }

  // This method will now use a PrimeNG confirmation dialog
  initiateSignOutProcess(event: Event): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translateServ.dictionary['CONFIRM_SIGN_OUT'] || 'Are you sure you want to sign out?',
      header: this.translateServ.dictionary['SIGN_OUT_HEADER'] || 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.signOut();
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translateServ.dictionary['CANCELLED'] || 'Cancelled',
          detail: this.translateServ.dictionary['ACTION_CANCELLED'] || 'Sign-out cancelled.',
          life: 3000
        });
      }
    });
  }

  public signOut(): void {
    console.log("Good Bye - Initiating final logout.");
    this.messageService.add({
      severity: 'success',
      summary: this.translateServ.dictionary['SIGNED_OUT_SUMMARY'] || 'Signed Out',
      detail: this.translateServ.dictionary['SIGNED_OUT_DETAIL'] || 'You have been successfully signed out.',
      life: 1000
    });
    // Every "not logged in" case, sign-out included, lands on the public landing page.
    this.exitService.cleanExit();
  }
  
  public navigateToProfile(): void {
    this.router.navigate(['/profile']);
  }

  public navigateToSettings(): void {
    this.router.navigate(['/orgmanagement/settings']);
  }

  private getGreeting(): string 
  {
    try 
    {
      const hour = new Date().getHours();

      if (hour >= 5 && hour < 12) 
      {
        return this.translateServ.dictionary['GREETING_G_MORNING'] || 'Good Morning';
      } 
      else if (hour >= 12 && hour < 17) 
      {
        return this.translateServ.dictionary['GREETING_G_AFTERNOON'] || 'Good Afternoon';
      } 
      else if (hour >= 17 && hour < 21) 
      {
        return this.translateServ.dictionary['GREETING_G_EVENING'] || 'Good Evening';
      } 
      else 
      {
        return this.translateServ.dictionary['GREETING_G_NIGHT'] || 'Good Night';
      }
    } 
    catch (e) 
    {
      return this.translateServ.dictionary['GREETING_HELLO'] || 'Hello';
    }
  }

  private getName(): Observable<string>
  {
    // AuthStateService already holds the name (from login or the whoami check on page load) -
    // no need for a separate API call/localStorage cache.
    const name = `${this.authStateService.getFirstName()} ${this.authStateService.getLastName()}`.trim();
    return of(name);
  }
}
